import { useQueries, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import { networkTypeSelector } from "store/userSliceHelpers";
import { getTokenSupport } from "utils/AxiosUtil";
import type { NetworkType } from "utils/network";
import { TokenSupport } from "types/tokenSupport";

const tokenSupportQuery = (networkType: NetworkType, token: string) => ({
  queryKey: ["tokenSupport", networkType, token],
  queryFn: () => getTokenSupport(token, networkType),
  retry: false,
});

/**
 * Whether the backend can actually settle in `token`.
 *
 * The portal decides WHICH tokens to offer; this is the one thing only the deploy knows — whether
 * the address is in the payments contract's registry. Keyed by network + token, at the client's
 * default staleTime: Infinity, since registration changes only via an owner-gated on-chain call.
 *
 * `undefined` while in flight or on error, which callers must read as "unknown", never as "no":
 * an unreachable backend is not evidence that a token is unsupported.
 */
export function useTokenSupport(token: string | undefined) {
  const { user } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  return useQuery<TokenSupport>({
    ...tokenSupportQuery(networkType, token!),
    enabled: !!user && !!token,
  });
}

/** useTokenSupport for several tokens, sharing its cache. Same "undefined is unknown" rule. */
export function useTokensSupport(tokens: string[]): (TokenSupport | undefined)[] {
  const { user } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  return useQueries({
    queries: tokens.map((token) => ({
      ...tokenSupportQuery(networkType, token),
      enabled: !!user,
    })),
    // Inline on purpose — a stable combine goes stale for a render when `enabled` flips.
    combine: (results) =>
      results.map((r) => r.data as TokenSupport | undefined),
  });
}
